import { useEffect, useState } from 'react'
import { Car, Fuel, Calendar, RefreshCw, Pencil, Trash2, User, UserCog, Droplet, Gauge, FileText, FileSpreadsheet, type LucideIcon } from 'lucide-react'
import api from '../services/api'
import LookupSelect from '../components/LookupSelect'
import { Linha, CampoCalc, larguraNumero } from './LancamentoDepositos'

interface Item {
  dpstip: string; dpsnum: number; dpsdesnum: number; veicod: string; veipla: string | null; data: string; hora: number
  motcod: string; motnome: string; abastecedor: string | null; abastecedor_nome: string | null; pracod: string
  litros: number; valor: number; vun: number; desconto: number; hodometro: number; media: number
  obs: number; combustivel: string; ctf: string
}

interface Resultado {
  veiculo: { veicod: string; veipla: string; combustivel: string; hodometro: number } | null
  itens: Item[]
  totais: { registros: number; litros: number; valor: number; mediaGeral: number }
}

const hoje = () => new Date().toISOString().slice(0, 10)
const fmtData = (iso: string) => iso.split('-').reverse().join('/')
const fmtNum = (n: number, casas: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })
// dpsdeshor guarda 7.09 = 07:09
const fmtHora = (h: number) => {
  const [hh, mm = '00'] = h.toFixed(2).split('.')
  return `${hh.padStart(2, '0')}:${mm}`
}

// Bloco com faixa colorida e título, pra destacar um grupo de campos.
function PainelDestaque({ titulo, icon: Icon, cor, style, children }: {
  titulo: string; icon: LucideIcon; cor: string
  style?: React.CSSProperties; children: React.ReactNode
}) {
  return (
    <div style={{
      border: `1px solid ${cor}33`, borderLeft: `4px solid ${cor}`, borderRadius: 10,
      background: `linear-gradient(135deg, ${cor}0f, ${cor}05)`, padding: '0.7rem 1rem 0.85rem', ...style,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.55rem', color: cor, fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        <Icon size={14} color={cor} /> {titulo}
      </div>
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>{children}</div>
    </div>
  )
}

// Atualiza lançamentos de combustível (GeneXus WDPS054 → WDPS056); sem veículo traz todos.
// Com arla: só lançamentos de ARLA, sem média km/l.
export default function AtualizarLancamentosVeiculo({ arla = false }: { arla?: boolean }) {
  // Vindo do painel do Combustível: ?veiculo=&ini=&fim= já consulta
  const [url] = useState(() => new URLSearchParams(window.location.search))
  const [veicod, setVeicod] = useState(url.get('veiculo') || '')
  const [pracod, setPracod] = useState('')
  const [pranom, setPranom] = useState('')
  const [dataIni, setDataIni] = useState(url.get('ini') || hoje())
  const [dataFim, setDataFim] = useState(url.get('fim') || hoje())

  const [res, setRes] = useState<Resultado | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [erro, setErro] = useState('')

  const [editando, setEditando] = useState<Item | null>(null)
  const [aviso, setAviso] = useState('')
  // Filtros da última consulta: o relatório sai igual ao que está na tela.
  const [filtros, setFiltros] = useState<{ veicod: string; pracod: string; dataIni: string; dataFim: string } | null>(null)
  const [gerando, setGerando] = useState<'' | 'pdf' | 'excel'>('')

  async function consultar(silencioso = false) {
    setErro('')
    if (!silencioso) setAviso('')
    setCarregando(true)
    const params = silencioso && filtros ? filtros : { veicod, pracod, dataIni, dataFim }
    try {
      const r = await api.get('/combustivel/abastecimentos-veiculo', { params: { ...params, arla: arla ? '1' : undefined } })
      setRes(r.data)
      setFiltros(params)
    } catch (e: any) {
      setRes(null)
      setErro(e?.response?.data?.error || 'Erro ao consultar lançamentos.')
    } finally {
      setCarregando(false)
    }
  }

  async function gerarRelatorio(formato: 'pdf' | 'excel') {
    if (!filtros) return
    setGerando(formato)
    setErro('')
    try {
      const r = await api.get(`/combustivel/abastecimentos-veiculo/${formato}`, { params: { ...filtros, arla: arla ? '1' : undefined }, responseType: 'blob' })
      const tipo = formato === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      const url = URL.createObjectURL(new Blob([r.data], { type: tipo }))
      if (formato === 'pdf') {
        window.open(url, '_blank')
      } else {
        const a = document.createElement('a')
        a.href = url
        a.download = arla ? 'lancamentos-arla.xlsx' : 'lancamentos-combustivel.xlsx'
        a.click()
        URL.revokeObjectURL(url)
      }
    } catch {
      setErro(formato === 'pdf' ? 'Erro ao gerar PDF.' : 'Erro ao gerar Excel.')
    } finally {
      setGerando('')
    }
  }

  useEffect(() => { if (url.get('veiculo')) consultar() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function excluir(i: Item) {
    const deposito = i.pracod.toUpperCase().startsWith('DP')
    if (!window.confirm(`Excluir o abastecimento ${i.dpsnum} de ${fmtData(i.data)} (${fmtNum(i.litros, 3)} L)?`
      + (deposito ? `\n\nOs litros voltam para o estoque do depósito ${i.pracod}.` : ''))) return
    try {
      await api.delete(`/combustivel/abastecimentos/${i.dpstip.trim()}/${i.dpsnum}/${i.dpsdesnum}`)
      setAviso(`Abastecimento ${i.dpsnum} excluído.`)
      consultar(true)
    } catch (e: any) {
      window.alert(e?.response?.data?.error || 'Erro ao excluir abastecimento.')
    }
  }

  const th: React.CSSProperties = {
    padding: '0.5rem 0.5rem', fontSize: '0.68rem', fontWeight: 800, color: '#fff', textAlign: 'left',
    background: '#1a6ea8', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.03em',
  }
  const td: React.CSSProperties = { padding: '0.35rem 0.5rem', fontSize: '0.78rem', borderBottom: '1px solid #eef2f7', whiteSpace: 'nowrap' }
  const num: React.CSSProperties = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
      <div className="modal-box" style={{ width: '100%', maxWidth: 680, maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a6ea8, #14547f)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
            <span style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,0.15)',
            }}>
              <RefreshCw size={18} color="#fff" />
            </span>
            <div>
              <h2>{arla ? 'Atualizar Lançamentos ARLA' : 'Atualizar Lançamentos'}</h2>
              <div style={{ fontSize: '0.7rem', color: '#dbeafe', marginTop: 2 }}>{arla ? 'Consulta e correção de lançamentos de ARLA32' : 'Consulta e correção de abastecimentos'}</div>
            </div>
          </div>
        </div>

        <div className="modal-corpo">
          <Linha label="Veículo" icon={Car}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
              <div style={{ maxWidth: 140 }}>
                <LookupSelect
                  endpoint="/erp/veiculos"
                  codKey="veicod"
                  descKey="veipla"
                  tagKey="veicod"
                  codValue={veicod}
                  descValue={veicod}
                  onSelect={cod => setVeicod(cod)}
                  placeholder="Todos"
                />
              </div>
              {veicod
                ? <button className="btn btn-secondary" style={{ padding: '0.25rem 0.6rem', fontSize: '0.72rem' }} onClick={() => setVeicod('')}>Todos</button>
                : <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>em branco = todos os veículos</span>}
            </div>
          </Linha>

          <Linha label="Posto/Local" icon={Fuel}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
              <LookupSelect
                endpoint="/combustivel/postos"
                codKey="pracod"
                descKey="pranom"
                tagKey="pracod"
                codValue={pracod}
                descValue={pranom}
                onSelect={(cod, desc) => { setPracod(cod); setPranom(desc) }}
                placeholder="Todos"
                listWidth={320}
              />
              {pracod
                ? <button className="btn btn-secondary" style={{ padding: '0.25rem 0.6rem', fontSize: '0.72rem' }} onClick={() => { setPracod(''); setPranom('') }}>Todos</button>
                : <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>em branco = todos</span>}
            </div>
          </Linha>

          <Linha label="Período" icon={Calendar}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <input type="date" value={dataIni} onChange={e => setDataIni(e.target.value)} style={{ maxWidth: 170 }} />
              <span style={{ fontSize: '0.8rem', color: '#57606f' }}>a</span>
              <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} style={{ maxWidth: 170 }} />
            </div>
          </Linha>

          {!veicod && (
            <div style={{
              marginTop: '0.9rem', fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8,
              background: '#e0effa', color: '#14547f', border: '1px solid #bfdbfe',
            }}>
              Nenhum veículo informado: a consulta vai trazer os lançamentos de todos os veículos no período.
            </div>
          )}

          {erro && (
            <div style={{
              marginTop: '0.9rem', fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8,
              background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca',
            }}>
              {erro}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-primary" onClick={() => consultar()} disabled={carregando}>
            {carregando ? 'Consultando...' : 'Consultar'}
          </button>
          {res && res.itens.length > 0 && (
            <>
              <button className="btn" style={{ background: '#dc2626', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                onClick={() => gerarRelatorio('pdf')} disabled={!!gerando}>
                <FileText size={15} /> {gerando === 'pdf' ? 'Gerando PDF...' : 'PDF'}
              </button>
              <button className="btn" style={{ background: '#15803d', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                onClick={() => gerarRelatorio('excel')} disabled={!!gerando}>
                <FileSpreadsheet size={15} /> {gerando === 'excel' ? 'Gerando Excel...' : 'Excel'}
              </button>
            </>
          )}
        </div>
      </div>

      {res && (
        <div className="modal-box" style={{ width: '100%', maxWidth: 'none', maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
          <div className="modal-corpo">
            {aviso && (
              <div style={{
                marginBottom: '0.9rem', fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8,
                background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0',
              }}>
                {aviso}
              </div>
            )}
            <PainelDestaque titulo="Veículo" icon={Car} cor="#1a6ea8" style={{ marginBottom: '0.9rem' }}>
              {res.veiculo ? (
                <>
                  <CampoCalc rotulo="VEÍCULO" valor={res.veiculo.veicod} largura={larguraNumero(6)} destaque />
                  <CampoCalc rotulo="PLACA" valor={res.veiculo.veipla || '—'} largura={larguraNumero(8)} destaque />
                  <CampoCalc rotulo="COMBUSTÍVEL" valor={res.veiculo.combustivel || '—'} largura={larguraNumero(7)} destaque />
                </>
              ) : (
                <CampoCalc rotulo="VEÍCULO" valor="todos" largura={larguraNumero(6)} destaque />
              )}
              <CampoCalc rotulo="PERÍODO" valor={`${fmtData(dataIni)} a ${fmtData(dataFim)}`} largura={larguraNumero(22)} destaque />
              <CampoCalc rotulo="POSTO" valor={pracod || 'todos'} largura={larguraNumero(6)} destaque />
              {res.veiculo && <CampoCalc rotulo="HODÔMETRO ATUAL" valor={fmtNum(res.veiculo.hodometro, 0)} largura={larguraNumero(9)} destaque />}
            </PainelDestaque>

            <PainelDestaque titulo={`Registros (${res.itens.length})`} icon={RefreshCw} cor="#1a6ea8">
            <style>{`.grade-abast tbody tr:nth-child(even) td { background: #f5f9fd; } .grade-abast tbody tr:hover td { background: #e0effa; }`}</style>
            <div style={{ width: '100%', overflowX: 'auto', border: '1px solid #bfdbfe', borderRadius: 8, background: '#fff' }}>
              <table className="grade-abast" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {!res.veiculo && <th style={th}>Veículo</th>}
                    <th style={th}>Data</th>
                    <th style={th}>Hora</th>
                    <th style={th}>Reg</th>
                    <th style={th}>Motorista</th>
                    <th style={th}>Posto</th>
                    <th style={{ ...th, textAlign: 'right' }}>Litros</th>
                    <th style={{ ...th, textAlign: 'right' }}>Valor</th>
                    <th style={{ ...th, textAlign: 'right' }}>Desconto</th>
                    <th style={{ ...th, textAlign: 'right' }}>Hodôm</th>
                    {!arla && <th style={{ ...th, textAlign: 'right' }}>Média</th>}
                    <th style={th}>Combus</th>
                    <th style={th}>Frentista</th>
                    <th style={{ ...th, textAlign: 'center' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {res.itens.length === 0 && (
                    <tr><td colSpan={(res.veiculo ? 13 : 14) - (arla ? 1 : 0)} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: '1.2rem' }}>Nenhum lançamento no período.</td></tr>
                  )}
                  {res.itens.map(i => (
                    <tr key={`${i.dpstip}-${i.dpsnum}-${i.dpsdesnum}`}>
                      {!res.veiculo && <td style={{ ...td, fontWeight: 600 }} title={i.veipla || ''}>{i.veicod}</td>}
                      <td style={td}>{fmtData(i.data)}</td>
                      <td style={td}>{fmtHora(i.hora)}</td>
                      <td style={td}>{i.dpsnum}</td>
                      <td style={{ ...td, fontWeight: 600 }} title={i.motcod}>{i.motnome || i.motcod}</td>
                      <td style={td}>{i.pracod}</td>
                      <td style={{ ...num, color: '#b91c1c' }}>{fmtNum(i.litros, 3)}</td>
                      <td style={num}>{fmtNum(i.valor, 2)}</td>
                      <td style={num}>{fmtNum(i.desconto, 2)}</td>
                      <td style={{ ...num, color: '#1d4ed8' }}>{i.hodometro}</td>
                      {!arla && <td style={{ ...num, color: '#b91c1c' }}>{fmtNum(i.media, 2)}</td>}
                      <td style={td}>{i.combustivel}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }} title={i.abastecedor || ''}>
                        {i.abastecedor_nome || i.abastecedor || <span style={{ color: '#cbd5e1' }}>—</span>}
                      </td>
                      <td style={{ ...td, textAlign: 'center' }}>
                        {i.dpstip.trim() === 'A'
                          ? <span style={{ fontSize: '0.68rem', color: '#94a3b8' }} title="Lançado em Acerto de Viagem">acerto</span>
                          : (
                            <div style={{ display: 'inline-flex', gap: '0.35rem' }}>
                              <button className="btn btn-secondary" style={{ padding: '0.2rem 0.45rem' }} title="Alterar" onClick={() => setEditando(i)}>
                                <Pencil size={13} />
                              </button>
                              <button className="btn btn-secondary" style={{ padding: '0.2rem 0.45rem', color: '#b91c1c' }} title="Excluir" onClick={() => excluir(i)}>
                                <Trash2 size={13} />
                              </button>
                            </div>
                          )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </PainelDestaque>

            <PainelDestaque titulo="Abastecimentos no período" icon={Fuel} cor="#047857" style={{ marginTop: '0.9rem' }}>
              <CampoCalc rotulo="QTDE" valor={String(res.totais.registros)} largura={larguraNumero(5)} destaque />
              <CampoCalc rotulo="LITROS" valor={fmtNum(res.totais.litros, 3)} largura={larguraNumero(11)} destaque />
              <CampoCalc rotulo="VALOR" valor={`R$ ${fmtNum(res.totais.valor, 2)}`} largura={larguraNumero(13)} destaque />
              {!arla && <CampoCalc rotulo="MÉDIA GERAL" valor={fmtNum(res.totais.mediaGeral, 2)} largura={larguraNumero(6)} destaque />}
            </PainelDestaque>
          </div>
        </div>
      )}

      {editando && (
        <ModalAlterarAbastecimento
          item={editando}
          onFechar={() => setEditando(null)}
          onSalvo={() => { setAviso(`Abastecimento ${editando.dpsnum} alterado.`); setEditando(null); consultar(true) }}
        />
      )}
    </div>
  )
}

// Altera motorista, frentista, litros (valor recalculado pelo preço unitário) e hodômetro.
function ModalAlterarAbastecimento({ item, onFechar, onSalvo }: { item: Item; onFechar: () => void; onSalvo: () => void }) {
  const [motcod, setMotcod] = useState(item.motcod)
  const [motnome, setMotnome] = useState(item.motnome || item.motcod)
  const [abacod, setAbacod] = useState(item.abastecedor || '')
  const [abanome, setAbanome] = useState(item.abastecedor_nome || item.abastecedor || '')
  const [litros, setLitros] = useState(String(item.litros))
  const [hodometro, setHodometro] = useState(String(item.hodometro))
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState('')

  const vun = item.vun > 0 ? item.vun : (item.litros > 0 ? item.valor / item.litros : 0)
  const litrosNum = Number(litros.replace(',', '.')) || 0
  const valor = Math.abs(litrosNum - item.litros) > 0.0005 ? Math.round(litrosNum * vun * 100) / 100 : item.valor
  const deposito = item.pracod.toUpperCase().startsWith('DP')

  async function salvar() {
    setErro('')
    if (litrosNum <= 0) { setErro('Número de litros inválido.'); return }
    setSalvando(true)
    try {
      await api.put(`/combustivel/abastecimentos/${item.dpstip.trim()}/${item.dpsnum}/${item.dpsdesnum}`, {
        motcod, abastecedor: abacod, litros: litrosNum, hodometro: Number(hodometro),
      })
      onSalvo()
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao alterar abastecimento.')
    } finally {
      setSalvando(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onFechar}>
      <div className="modal-box" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a6ea8, #14547f)' }}>
          <div>
            <h2>Alterar Abastecimento {item.dpsnum}</h2>
            <div style={{ fontSize: '0.7rem', color: '#dbeafe', marginTop: 2 }}>
              {fmtData(item.data)} às {fmtHora(item.hora)} · posto {item.pracod}
            </div>
          </div>
        </div>

        <div className="modal-corpo">
          <Linha label="Motorista" icon={User}>
            <LookupSelect
              endpoint="/combustivel/motoristas"
              codKey="motcod"
              descKey="motnome"
              tagKey="motcod"
              codValue={motcod}
              descValue={motnome}
              onSelect={(cod, desc) => { setMotcod(cod); setMotnome(desc) }}
              placeholder="Buscar motorista..."
              listWidth={320}
            />
          </Linha>

          <Linha label="Frentista" icon={UserCog}>
            <LookupSelect
              endpoint="/combustivel/motoristas"
              codKey="motcod"
              descKey="motnome"
              tagKey="motcod"
              codValue={abacod}
              descValue={abanome}
              onSelect={(cod, desc) => { setAbacod(cod); setAbanome(desc) }}
              placeholder="Buscar frentista..."
              listWidth={320}
            />
          </Linha>

          <Linha label="Litros" icon={Droplet}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
              <input type="number" step="0.001" min="0" value={litros} onChange={e => setLitros(e.target.value)} style={{ width: larguraNumero(9) }} />
              <span style={{ fontSize: '0.78rem', color: '#57606f' }}>
                Valor: <strong style={{ color: '#1a6ea8' }}>R$ {fmtNum(valor, 2)}</strong>
                <span style={{ color: '#94a3b8' }}> ({fmtNum(vun, 3)}/L)</span>
              </span>
            </div>
          </Linha>

          <Linha label="Hodômetro" icon={Gauge}>
            <input type="number" min="0" step="1" value={hodometro} onChange={e => setHodometro(e.target.value)} style={{ width: larguraNumero(9) }} />
          </Linha>

          {deposito && (
            <div style={{ marginTop: '0.8rem', fontSize: '0.74rem', color: '#57606f' }}>
              Abastecimento de depósito: alterar os litros corrige também o estoque do {item.pracod}.
            </div>
          )}

          {erro && (
            <div style={{
              marginTop: '0.9rem', fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8,
              background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca',
            }}>
              {erro}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onFechar} disabled={salvando}>Cancelar</button>
          <button className="btn btn-primary" onClick={salvar} disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar'}</button>
        </div>
      </div>
    </div>
  )
}
